import { useMemo, useState } from "react";
import { useCommunityGrowthQuery } from "./useCommunityGrowthQuery";
import { ChartDataPoint, Year } from "@/util/chartUtils";
import { getDateString } from "@/util/dateUtils";
import { subYears } from "date-fns";
import { ApolloError } from "@apollo/client";

export type GrowthChartData = Partial<Record<Year, ChartDataPoint[]>>;

export const useCommunityGrowthSeries = (
  cohortYear: number | undefined,
  orgId: string | undefined,
  startDate: Date,
  endDate: Date,
): {
  data: GrowthChartData | undefined;
  loading: boolean;
  errors: ApolloError[] | undefined;
} => {
  const {
    data: line1,
    loading: loadingLine1,
    error: errorLine1,
  } = useCommunityGrowthQuery(
    cohortYear,
    orgId,
    getDateString(startDate),
    getDateString(endDate),
  );

  let yearOffset = 1;
  const {
    data: line2,
    loading: loadingLine2,
    error: errorLine2,
  } = useCommunityGrowthQuery(
    cohortYear,
    orgId,
    getDateString(subYears(startDate, yearOffset)),
    getDateString(subYears(endDate, yearOffset)),
    yearOffset,
  );

  yearOffset = 2;
  const {
    data: line3,
    loading: loadingLine3,
    error: errorLine3,
  } = useCommunityGrowthQuery(
    cohortYear,
    orgId,
    getDateString(subYears(startDate, yearOffset)),
    getDateString(subYears(endDate, yearOffset)),
    yearOffset,
  );

  yearOffset = 3;
  const {
    data: line4,
    loading: loadingLine4,
    error: errorLine4,
  } = useCommunityGrowthQuery(
    cohortYear,
    orgId,
    getDateString(subYears(startDate, yearOffset)),
    getDateString(subYears(endDate, yearOffset)),
    yearOffset,
  );

  yearOffset = 4;
  const {
    data: line5,
    loading: loadingLine5,
    error: errorLine5,
  } = useCommunityGrowthQuery(
    cohortYear,
    orgId,
    getDateString(subYears(startDate, yearOffset)),
    getDateString(subYears(endDate, yearOffset)),
    yearOffset,
  );

  yearOffset = -1;
  const {
    data: line6,
    loading: loadingLine6,
    error: errorLine6,
  } = useCommunityGrowthQuery(
    cohortYear,
    orgId,
    getDateString(subYears(startDate, yearOffset)),
    getDateString(subYears(endDate, yearOffset)),
    yearOffset,
  );

  const [chartData, setChartData] = useState<
    Partial<Record<Year, ChartDataPoint[]>> | undefined
  >(undefined);

  useMemo(() => {
    if (line1 && line2 && line3 && line4 && line5 && line6 && cohortYear) {
      setChartData({
        [`${cohortYear + 1}`]: line6,
        [`${cohortYear}`]: line1,
        [`${cohortYear - 1}`]: line2,
        [`${cohortYear - 2}`]: line3,
        [`${cohortYear - 3}`]: line4,
        [`${cohortYear - 4}`]: line5,
      });
    }
  }, [line1, line2, line3, line4, line5, line6, cohortYear]);

  const loading =
    loadingLine1 ||
    loadingLine2 ||
    loadingLine3 ||
    loadingLine4 ||
    loadingLine5 ||
    loadingLine6;

  const errors = [
    errorLine1,
    errorLine2,
    errorLine3,
    errorLine4,
    errorLine5,
    errorLine6,
  ].filter((error) => error !== undefined);

  return { data: chartData, loading, errors };
};
